/**
 * ModuleScreen displays the content of a selected module, including its tasks and details.
 * It provides both desktop and mobile layouts:
 * - Desktop: Shows task list sidebar with task details in main area
 * - Mobile: Shows task cards in a scrollable list
 *
 * The screen manages task selection and module submission, using AppStateStore
 * as the single source of truth for selection state.
 */

import React from 'react';
import { Platform } from 'react-native';
import {
  Center, Spinner, VStack, HStack, Text, useMedia, Box,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useModuleAndTaskSelectionState } from '../../common/context/AppStateStore';

import CurrentModuleCard from './components/CurrentModuleCard';
import TaskList from './components/TaskList';
import TaskListCards from './components/TaskListCards';
import TaskPreviewSelector from './components/TaskPreviewSelector';
import TaskSidebarSelector from './components/TaskSidebarSelector';
import SubmitModuleNotification from './components/SubmitModuleNotification';
import ModuleTaskPreviewErrorBoundary from './components/ModuleTaskPreviewErrorBoundary';

import { useSelectedModule } from '../../common/hooks/student/useSelectedModule';
import { useSelectedTask } from '../../common/hooks/student/useSelectedTask';
import { useActivity } from '../../common/hooks/student/useActivity';

import { useDialog } from '../../common/context/DialogContext';
import ScrollableScreenContainer from '../../common/components/ScrollableScreenContainer';
import { useAuthStore } from '../../common/auth/store';
import { useGetUserBookings } from '../../common/api/endpoints/appointments/appointments';
import log from '../../common/utils/logger';
import { ModuleUpgradeOfferBanner } from './components/ModuleUpgradeOfferBanner';
import { useCompleteModuleSubmissionUx } from '../../common/hooks/student/module/useCompleteModuleSubmissionUx';
import { useLeaveTerminalModule } from './hooks/useLeaveTerminalModule';

const ModuleScreen = () => {
  const {
    module, tasks, isModulesLoading, isTasksLoading, submitModuleMutation,
  } = useSelectedModule();

  const {
    selectedModule, taskId, setSelectedTask,
  } = useModuleAndTaskSelectionState();

  // Scroll ref for scrolling to top when task is selected
  const scrollRef = React.useRef(null);

  const { logSelectTask, logSubmitModule } = useActivity();
  const media = useMedia();
  const { task, allTasksCompleted: allTasksDone } = useSelectedTask();
  const isSubmittedOrGraded = module?.state?.studentWorkflowState === 'submitted'
    || module?.state?.studentWorkflowState === 'graded';
  const allTasksCompleted = allTasksDone && !isSubmittedOrGraded;

  const { t } = useTranslation();
  const { showDialog, hideDialog, showToastMessage } = useDialog();
  const navigation = useNavigation();
  const completeModuleSubmissionUx = useCompleteModuleSubmissionUx();
  const leaveTerminalModule = useLeaveTerminalModule({
    module,
    isLoading: isModulesLoading,
    courseId: selectedModule?.courseId || module?.courses?.[0]?.id,
    navigation,
  });

  // Prefetch bookings if at least one event task exists
  const hasEventTask = React.useMemo(
    () => tasks?.some((t) => t.type === 'event' || (t.type === 'multiple_choice' && !!t?.eventConfig?.eventKindId)),
    [tasks],
  );
  const userId = useAuthStore((s) => s.user.id);
  useGetUserBookings(userId, undefined, { query: { enabled: hasEventTask } });

  if (leaveTerminalModule) {
    return null;
  }

  if (isModulesLoading || isTasksLoading) {
    return (
      <Center testID="loading-container" flex={1}>
        <Spinner testID="loading-indicator" />
      </Center>
    );
  }

  if (!module || !tasks) {
    return (
      <Center flex={1}>
        <Text>{t('student.module.messages.no-module-selected')}</Text>
      </Center>
    );
  }

  /**
   * Opens the task screen for the currently selected task.
   * Only used in desktop view where task screen is separate.
   */
  const handleOpenTask = async () => {
    navigation.navigate('TaskScreen', {
      ...selectedModule,
      taskId,
    });
    await logSelectTask({ ...selectedModule, taskId });
  };

  /**
   * Handles selection of a task, updating store state and navigating if on mobile.
   * @param {Object} selectedTask - The task being selected
   */
  const handleSelectTask = async (selectedTask) => {
    if (!module || !selectedTask) return;
    setSelectedTask(module.id, selectedTask.id);

    // Determine if we're in desktop mode (web + medium breakpoint or larger)
    const isDesktop = Platform.OS === 'web' && media.md;

    // Desktop: Scroll to top (stay on same screen, show in preview panel)
    if (isDesktop && scrollRef.current) {
      scrollRef.current.scrollTo({ y: 0, animated: true });
    }

    // Mobile (native apps OR narrow browser): Navigate to TaskScreen for ALL tasks
    if (!isDesktop) {
      navigation.navigate('TaskScreen', {
        ...selectedModule,
        taskId: selectedTask.id,
      });
      await logSelectTask({
        ...selectedModule,
        taskId: selectedTask.id,
      });
    }
  };

  /**
   * Handles module submission after all tasks are completed.
   * Shows confirmation dialog and handles the submission process.
   */
  const handleSubmitModule = () => {
    if (allTasksCompleted) {
      showDialog({
        title: t('student.module.submit-module-dialog.title'),
        message: t('student.module.submit-module-dialog.complete-message'),
        buttons: [
          {
            text: t('common.buttons.cancel'),
            action: 'secondary',
            testID: 'button-dialog-cancel-submit-module',
          },
          {
            text: t('student.module.submit-module-dialog.button-submit'),
            testID: 'button-dialog-submit-module',
            // Use mutate (not mutateAsync): TanStack Query still rejects mutateAsync
            // after onError runs, which becomes an unhandledrejection → Sentry
            // (FLARE-63) even when MODULE_SUBMISSION_INCOMPLETE is an expected 422.
            onPress: () => {
              submitModuleMutation.mutate(selectedModule, {
                onSuccess: () => {
                  logSubmitModule(selectedModule);
                  completeModuleSubmissionUx({
                    moduleTitle: module.title,
                    courseId: selectedModule.courseId,
                    moduleId: selectedModule.moduleId,
                  });
                },
                onError: (error) => {
                  const apiErr = error?.response?.data?.error;
                  if (apiErr?.message || apiErr?.code) {
                    showToastMessage(
                      'error',
                      `${apiErr?.message || 'Submission failed'} (${apiErr?.code || error?.response?.status})`,
                    );
                    showDialog({
                      title: t('student.module.submit-module-dialog.title'),
                      message: `${apiErr?.message || 'Submission failed'} (${apiErr?.code || error?.response?.status})`,
                      buttons: [
                        {
                          text: t('common.buttons.ok'),
                        },
                      ],
                    });
                  } else {
                    showToastMessage('error', t('student.module.submit-module-dialog.error-message'));
                  }
                  log.info('Error when submitting module', error);
                },
              });
            },
          },
        ],
      });
    } else {
      showDialog({
        title: t('student.module.submit-module-dialog.title'),
        message: t('student.module.submit-module-dialog.incomplete-message'),
        buttons: [
          {
            text: t('common.buttons.ok'),
            onPress: () => hideDialog(),
          },
        ],
      });
    }
  };

  return (
    <ScrollableScreenContainer scrollRef={scrollRef} bg="$bgApp">
      <VStack space="lg" mb="$8">
        {module.upgradeOffer && selectedModule?.courseId ? (
          <ModuleUpgradeOfferBanner
            courseId={selectedModule.courseId}
            moduleId={module.id}
            upgradeOffer={module.upgradeOffer}
          />
        ) : null}
        {Platform.OS === 'web' && (
          <CurrentModuleCard module={module} course={module.courses[0]} $base-display="none" $md-display="flex" />
        )}
        <SubmitModuleNotification
          visible={allTasksCompleted}
          isSubmitted={isSubmittedOrGraded}
          handleSubmitModule={handleSubmitModule}
        />
        <HStack space="lg">
          {Platform.OS === 'web' && media.md ? (
            /* Desktop View */
            <>
              <TaskList
                $md-w={250}
                $lg-w={300}
                module={module}
                tasks={tasks}
                currentTask={task}
                handleSelectTask={handleSelectTask}
                selectedModule={selectedModule}
              />

              <VStack flex={1} space="lg">
                <ModuleTaskPreviewErrorBoundary task={task} module={module}>
                  <TaskPreviewSelector
                    task={task}
                    loading={isTasksLoading}
                    module={module}
                    tasks={tasks}
                    selectedModule={selectedModule}
                    handleOpenTask={handleOpenTask}
                  />
                </ModuleTaskPreviewErrorBoundary>

                {!task && !allTasksCompleted && (
                  <Center flex={1}>
                    <Text size="sm">{t('student.module.messages.no-task-selected')}</Text>
                  </Center>
                )}

                {/* Show sidebar below preview on medium screens (when right column is hidden) */}
                {!media.xl && <TaskSidebarSelector task={task} tasks={tasks} module={module} />}
              </VStack>

              {/* Right column sidebar - only on extra large screens */}
              {media.xl && (
                <Box w={200}>
                  <TaskSidebarSelector task={task} tasks={tasks} module={module} />
                </Box>
              )}
            </>
          ) : (
            /* Mobile View */
            <VStack w="100%" my="$8" space="lg">
              <TaskListCards
                module={module}
                tasks={tasks}
                currentTask={task}
                handleSelectTask={handleSelectTask}
                selectedModule={selectedModule}
              />
            </VStack>
          )}
        </HStack>
      </VStack>
    </ScrollableScreenContainer>
  );
};
export default ModuleScreen;
